import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useInspect } from '@/designs-dev/inspect/InspectContext';
import {
  baseName,
  buildLayerTree,
  type LayerNode,
} from '@/designs-dev/inspect/fiber';

// Depth-first search of the layer tree for the first node whose component name
// matches (ignoring Typography variant / ×N suffixes). Backs the ?select pin.
export const findByName = (
  nodes: LayerNode[],
  name: string,
): LayerNode | null => {
  for (const n of nodes) {
    if (baseName(n.inspection.name) === name) return n;
    const inChild = findByName(n.children, name);
    if (inChild) return inChild;
  }
  return null;
};

// Reflects drill-mode state into the URL, so a drilled view is a shareable
// address: `?drill=1` for inspect mode and `?select=<Component>` for the pinned
// selection. Built on the changelog deep-link (?select), now persisted.
//
// The sync is deliberately ONE-DIRECTIONAL (state → URL) to avoid the classic
// reader-vs-writer fight on a deep-link load, where the initial state disagrees
// with the address bar. The initial state is instead seeded from the URL
// (InspectProvider reads ?drill / ?select at mount); after that, user actions
// drive the state and these writers mirror it. The only URL → state path is the
// ?select poll below, which pins a deep-linked component once it has mounted.
// Writes are guarded (only when the value differs) and use a functional update,
// so a stage's `?scene` is never clobbered.
export const useInspectUrlSync = (screenEl: HTMLElement | null) => {
  const { mode, setActive, current, setCurrent } = useInspect();
  const [searchParams, setSearchParams] = useSearchParams();

  const selectParam = searchParams.get('select');
  const currentName = current ? baseName(current.name) : null;

  // mode -> ?drill (1 = inspect). Leaving drill (view) also drops ?select (no
  // selection makes sense outside the drill chrome).
  useEffect(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (mode === 'inspect') {
          next.set('drill', '1');
        } else {
          next.delete('drill');
          next.delete('select');
        }
        return next.toString() === prev.toString() ? prev : next;
      },
      { replace: true },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  // current -> ?select. Additive: it sets/updates the pinned component but never
  // removes the param when the selection clears, so an async deep-link pin (the
  // poll below) is never stripped before it lands. Removal rides on leaving
  // drill mode, above.
  useEffect(() => {
    if (!currentName) return;
    setSearchParams(
      (prev) => {
        if (prev.get('select') === currentName) return prev;
        const next = new URLSearchParams(prev);
        next.set('select', currentName);
        return next;
      },
      { replace: true },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentName]);

  // ?select -> current: pin the named component once it has mounted. The target
  // may render a tick after the screen does, so poll the live tree briefly.
  useEffect(() => {
    if (!selectParam || !screenEl) return;
    if (currentName === selectParam) return; // already pinned
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let tries = 0;
    const attempt = () => {
      if (cancelled) return;
      const match = findByName(buildLayerTree(screenEl), selectParam);
      if (match) {
        setActive(true);
        setCurrent(match.inspection);
        match.inspection.domNode?.scrollIntoView({
          block: 'center',
          behavior: 'smooth',
        });
        return;
      }
      if (tries++ < 20) timer = setTimeout(attempt, 100);
    };
    attempt();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectParam, screenEl]);
};
